import type { AuditRunSummary } from '@owl/shared';
import { useEffect, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { formatUsd } from '../components/charts/AiUsageBlocks';
import { activateOnKey } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/time';

function passPct(r: AuditRunSummary): number {
  const scored = r.passCount + r.partialCount + r.failCount;
  return scored > 0 ? Math.round((100 * r.passCount) / scored) : 0;
}

function StatusCell({ r }: { r: AuditRunSummary }) {
  if (r.status === 'running') {
    return (
      <span className="mono" style={{ fontSize: 12, color: 'var(--accent-2, #2b5fb3)' }}>
        running {r.processed}/{r.total}
      </span>
    );
  }
  if (r.status === 'error') {
    return <span style={{ fontSize: 12, color: 'var(--danger, #d9534f)' }}>error</span>;
  }
  return (
    <span className="dim" style={{ fontSize: 12 }}>
      done
    </span>
  );
}

export function Audits({ ctx }: { ctx: ShellCtx }) {
  const [items, setItems] = useState<AuditRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on refreshTick
  useEffect(() => {
    let active = true;
    api
      .listAuditRuns()
      .then((res) => {
        if (active) setItems(res.items);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      active = false;
    };
  }, [ctx.refreshTick]);

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Audits</div>
          <div className="view-sub">
            {items?.length ?? 0} runs · the judge over batches of real conversations
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No audits yet. Start one from the Conversations view with “Auditar lote”.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Status</th>
                <th>Model</th>
                <th>Pass</th>
                <th>P / Pa / F / na / err</th>
                <th>Avg score</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="data-row"
                  tabIndex={0}
                  onClick={() => ctx.navigate({ name: 'auditdetail', id: r.id })}
                  onKeyDown={(e) =>
                    activateOnKey(e, () => ctx.navigate({ name: 'auditdetail', id: r.id }))
                  }
                >
                  <td>
                    <div>
                      <strong>{r.label || formatDateTime(r.createdAt)}</strong>
                    </div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      {formatDateTime(r.createdAt)} · {r.applicationName ?? 'all apps'}
                    </div>
                  </td>
                  <td>
                    <StatusCell r={r} />
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {r.model}
                  </td>
                  <td>
                    <strong>{passPct(r)}%</strong>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {r.passCount} / {r.partialCount} / {r.failCount} / {r.naCount} / {r.errorCount}
                  </td>
                  <td className="mono">{r.avgScore === null ? '—' : r.avgScore}</td>
                  <td className="mono">{formatUsd(r.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
